"use client";
import ChangeCoverIcon from "@/assets/icons/background.svg";
import CameraIcon from "@/assets/icons/camera.svg";
import { Theme } from "@/shared/constants";
import styles from "./CenteredHeader.module.css";
import HeaderCoverGray from "@/assets/graphics/backgrounds/header-cover-gray.png";
import HeaderCoverColorful from "@/assets/graphics/backgrounds/header-cover-colorful.png";
import EndyPlaceholderIcon from "@/assets/icons/endy/endy-placeholder.svg";
import { UploadFile } from "../UploadFile";
import { twMerge } from "tailwind-merge";
import { empty } from "@/shared/helpers";
import { SUPPORTED_IMAGE_TYPES, SUPPORTED_NFT_TYPES } from "@/shared/media";
import useTheme from "@/shared/hooks/useTheme";
import { ImageTypeHandler } from "../ImageTypeHandler";
import { EndemicImage } from "../Image";
import { ImagePlaceholder } from "../Image/types";

export interface Props {
  title?: string | null;
  subtitle?: string | React.ReactNode;
  imageUrl?: string | null;
  imagePlaceholder?: ImagePlaceholder | null;
  coverSrc?: string | null;
  coverPlaceholder?: ImagePlaceholder | null;
  headerTitle?: string | null;
  insideForm?: boolean;
  imageError?: string | boolean;
  headerError?: string | boolean;
  headerImageFieldName?: string;
  imageFieldName?: string;
  setHeaderLocalPath?: (localPath: string) => void;
  setImageLocalPath?: (localPath: string) => void;
  disabled?: boolean;
  className?: string;
  collectionImageClassName?: string;
  collectionCoverClassName?: string;
  imageId?: string;
  headerImageId?: string;
  imageContainerClassName?: string;
  headerImageContainerClassName?: string;
}

/**
 * TODO:
 * Fix fonts
 * Colors hardcoded
 */
const CenteredHeader = ({
  insideForm,
  title,
  subtitle,
  imageUrl,
  imagePlaceholder,
  coverSrc,
  coverPlaceholder,
  headerImageFieldName,
  imageFieldName,
  headerTitle,
  imageError,
  headerError,
  setHeaderLocalPath = empty,
  setImageLocalPath = empty,
  className,
  collectionImageClassName,
  collectionCoverClassName,
  disabled,
  imageId,
  headerImageId,
  imageContainerClassName,
  headerImageContainerClassName,
}: Props) => {
  const theme = useTheme();

  return (
    <header className={twMerge("relative", className)}>
      <section
        className={twMerge(
          "flex relative gap-x-[200px] lg:rounded-3xl w-full h-36 lg:h-96 border-x-0 overflow-hidden",
          headerError ? "border-2 border-red" : "",
          headerImageContainerClassName,
        )}
        id={headerImageId}
      >
        {/**solution for cover img border */}
        <div className="h-full w-full lg:rounded-l-3xl border-grey endemic:border-white border-y lg:border-l border-right-0"></div>
        <div className="h-full w-full lg:rounded-r-3xl border-grey endemic:border-white border-y lg:border-r border-right-0"></div>
        <div className="absolute top-0 w-[200px] left-1/2 -translate-x-1/2 border-grey endemic:border-white border-t bg-transparent"></div>

        {coverSrc ? (
          <EndemicImage
            src={coverSrc}
            placeholder={coverPlaceholder}
            alt={"cover image"}
            className={`object-cover ${styles.mask}`}
            fill
            sizes="100%"
            dynamic
          />
        ) : (
          <EndemicImage
            alt={"cover image"}
            src={
              theme === Theme.Endemic ? HeaderCoverGray : HeaderCoverColorful
            }
            className={`object-cover ${styles.mask}`}
            fill
          />
        )}
        {insideForm && headerImageFieldName ? (
          <UploadFile
            fieldName={headerImageFieldName}
            title={headerTitle}
            startIcon={<ChangeCoverIcon className="w-5 h-4 fill-white" />}
            className={twMerge(
              "absolute flex items-center justify-center top-7 right-7 bg-red rounded-3xl h-12 w-12 lg:h-10 lg:w-auto lg:px-4 border-4 border-invert endemic:border-primary cursor-pointer",
              collectionCoverClassName,
            )}
            setLocalPath={setHeaderLocalPath}
            accept={SUPPORTED_NFT_TYPES}
            maxFileSize={5}
            disabled={disabled}
          />
        ) : null}
      </section>
      <section
        className={twMerge(
          "flex flex-col items-center",
          imageContainerClassName,
        )}
        id={imageId}
      >
        <div className={"relative flex -mt-20 w-[160px] h-[160px]"}>
          {imageUrl ? (
            <div className="relative h-full w-full rounded-full bg-primary endemic:bg-transparent overflow-hidden box-content">
              <ImageTypeHandler
                src={imageUrl}
                placeholder={imagePlaceholder}
                alt={"profile image"}
                fill
                className="object-cover"
              />
            </div>
          ) : (
            <div
              className={twMerge(
                "flex rounded-full justify-center items-center endemic:border-gray border h-full w-full bg-primary endemic:bg-transparent overflow-hidden box-content",
                imageError ? " border-red" : "",
              )}
            >
              <EndyPlaceholderIcon />
            </div>
          )}
          <div className="flex absolute -bottom-1 -right-1 items-center justify-center p-1 rounded-full bg-primary endemic:bg-gradient-to-r endemic:from-[#3C7887] endemic:to-[#417385]">
            {insideForm && imageFieldName ? (
              <UploadFile
                fieldName={imageFieldName}
                startIcon={<CameraIcon className="w-5 h-4 fill-white" />}
                className={twMerge(
                  "flex flex-col rounded-full items-center justify-center bg-red h-10 w-10",
                  collectionImageClassName,
                )}
                setLocalPath={setImageLocalPath}
                accept={SUPPORTED_IMAGE_TYPES}
                maxFileSize={5}
                disabled={disabled}
              />
            ) : null}
          </div>
        </div>
        {headerError || imageError ? (
          <div className="absolute -bottom-4 text-red">
            {headerError || imageError}
          </div>
        ) : null}
        <div className="flex flex-col items-center">
          <div className="font-semibold tracking-tighter text-3xl lg:text-5xl text-primary mt-6 mb-2">
            {title}
          </div>
          <div className="text-base lg:text-lg leading-5 text-secondary endemic:text-grey text-center">
            {subtitle}
          </div>
        </div>
      </section>
    </header>
  );
};

export default CenteredHeader;
